import { FC, createElement, Fragment, ReactNode, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import cn from "classnames";
import BannerCard from "src/components/BannerCard/BannerCard";
import { MdArrowForwardIos } from "react-icons/md";
import { Button, Drawer, Empty, Grid, Modal, Segmented } from "antd";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n";
import { Timeline } from "src/components";
import { AirplaneIcon, InfoIcon } from "src/assets/Icons";
import Carrier from "@Components/Carrier/Carrier";
import NoData from "@Components/NoData/NoData";
import {
  parse,
  type StyleOptions,
  type ElementNode,
  type ListItemNode,
  type ListNode,
  type PairNode,
  type ParsedNode,
  type RawNode,
  type TableNode,
  type TextNode,
} from "src/htmlParser";
import { MOBILE_BREAKPOINT_KEY } from "src/constants";
import { withConditionalComponent } from "src/HOC/WithMobileComponent";
import SkeletonBox from "src/components/SkeletonBox/SkeletonBox";
import { formatNumber } from "src/utility";
import type { FlightOrderDetails, FlightOrderFareRule } from "src/store/slices/client/client.api";
import {
  AddOnSummaryRow,
  AggregatedBaggage,
  AggregatedMeal,
  AggregatedSeat,
  MODAL_TABS,
  SSRFlight,
  TabKey,
  TransformedSegment,
} from "../constants";
import { buildAddOnSummaryFromSegments, hasAnySSR, toDisplayItems, transformFlightOrderDetails } from "../utils";
import ContactUs from "../../ContactUs/ContactUs";

const PaymentChip: FC<{
  rows: AddOnSummaryRow[];
  title: string;
  onClick?: () => void;
  currency: string;
  overrideTotal?: number;
}> = ({
  rows,
  title,
  onClick,
}) => {
  const { t } = useTranslation();
  return (
    <div className="outline-border-minimal flex flex-col gap-2 rounded-md p-3 outline">
      <div className="flex items-center justify-between">
        <div className="text-font-default text-sm font-semibold">{title}</div>
        {onClick && (
          <Button
            type="link"
            icon={<MdArrowForwardIos className="w-4! h-4! rtl:-scale-x-100" />}
            iconPlacement="end"
            className="text-sm! flex! items-center! m-0! p-0! text-font-active!"
            classNames={{ content: "p-0!" }}
            onClick={onClick}
          >
            {t("UserHistory.common.viewDetails")}
          </Button>
        )}
      </div>
      <div className="bg-border-minimal h-px w-full" />
      <div className="flex flex-col gap-2">
        {rows.map((row, i) => (
          <div key={i} className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-normal">
              {row.icon && <div className="text-icon-active">{row.icon}</div>}
              <div className="text-font-subtle">{row.label}</div>
            </div>
            <div className="text-font-default text-sm font-semibold">
              {formatNumber(row.total)} {row.currency}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

type BreakdownCardHeader = { icon?: ReactNode; label: ReactNode; value?: ReactNode };
type BreakdownCardRow = { label: ReactNode; value: ReactNode };

export const BreakdownCard: FC<{ header?: BreakdownCardHeader; rows: BreakdownCardRow[] }> = ({ header, rows }) => {
  return (
    <div className="bg-background-default outline-border-subtle flex flex-col gap-3 rounded-md p-3 outline">
      {header && (
        <>
          <div className="flex items-center justify-between gap-2">
            <div className="text-font-default flex items-center gap-2 text-sm font-semibold">
              {header.icon && <span className="text-icon-active">{header.icon}</span>}
              {header.label}
            </div>
            {header.value !== undefined && <div className="text-font-default text-base font-semibold">{header.value}</div>}
          </div>
          <div className="bg-border-default h-px w-full" />
        </>
      )}
      <div className="flex flex-col gap-2">
        {rows.map((row, i) => (
          <div key={i} className="flex items-center justify-between gap-2">
            <div className="text-font-subtle text-sm">{row.label}</div>
            <div className="text-font-default text-sm">{row.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ModalSegment: FC<{ segments: TransformedSegment[]; onChange: (val: number) => void; value: number }> = ({ segments, onChange, value }) => {
  if (segments.length <= 1) return null;
  const options = segments.map((seg, i) => ({
    label: seg.direction === "Outbound" ? i18n.t("UserHistory.flight.departure") : i18n.t("UserHistory.flight.return"),
    value: i + 1,
  }));
  return <Segmented options={options} onChange={onChange} value={value} />;
};

const ModalTabs: FC<{ value: TabKey; onChange: (key: TabKey) => void }> = ({ value, onChange }) => {
  const handleClick = (key: TabKey) => () => {
    onChange?.(key);
  };
  return (
    <div className="border-border-subtle flex w-full border-b">
      {MODAL_TABS.map((tab) => (
        <div
          role="button"
          key={tab.key}
          onClick={handleClick(tab.key)}
          className={`p-4! flex flex-1 cursor-pointer items-center justify-center gap-2 border-b-2 py-2 text-sm font-medium transition-colors
            ${value === tab.key ? "border-border-active text-font-active" : "text-font-minimal hover:text-font-subtle border-transparent"}`}
        >
          <div className="w-5">{tab.icon}</div>
          <div>{tab.label}</div>
        </div>
      ))}
    </div>
  );
};

const FlightSSRSection: FC<{ ssrFlight: SSRFlight<AggregatedMeal | AggregatedBaggage | AggregatedSeat>; tab: TabKey }> = ({ ssrFlight, tab }) => {
  const origin = ssrFlight.flight.origin.cityName || ssrFlight.flight.origin.code;
  const destination = ssrFlight.flight.destination.cityName || ssrFlight.flight.destination.code;
  const items = toDisplayItems(tab, ssrFlight);

  return (
    <div className="bg-background-default flex flex-col gap-4 rounded-md p-3">
      <div className="text-font-minimal flex items-center justify-between gap-2 text-sm font-semibold">
        <div className="text-font-informative bg-background-informative flex flex-1 items-center justify-center rounded-sm px-1 py-2">
          {origin}
        </div>
        <div className="flex-3">
          <Timeline
            items={[<></>, <></>]}
            connectorOverlays={[<AirplaneIcon className="text-icon-secondary h-5 w-5 " />]}
            className="flex-1!"
            classNames={{
              connector: {
                rail: "h-px border-dashed border-t border-border-default!",
                head: "w-0! h-0!",
                overlay: "bg-background-default! -translate-y-1/2!",
              },
            }}
          />
        </div>
        <div className="text-font-informative bg-background-informative flex flex-1 items-center justify-center rounded-sm px-1 py-2">
          {destination}
        </div>
      </div>
      <div className="flex flex-col gap-4">
        {items.map((item, i) => (
          <div key={i} className="flex items-start justify-between gap-2">
            <div className="text-font-minimal flex flex-col">
              {item.label}
              {item.quantity > 1 && <span className="ms-1">x{item.quantity}</span>}
            </div>
            <div className="text-font-default ms-4 shrink-0 font-semibold">
              {formatNumber(item.price * item.quantity)} {item.currency}
              {item.quantity > 1 && (
                <div className="text-font-minimal ms-1 text-xs font-normal">
                  ({formatNumber(item.price)} x{item.quantity})
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ModalView: FC<{ segments: TransformedSegment[]; addOnRows: AddOnSummaryRow[] }> = ({ segments, addOnRows }) => {
  const { t } = useTranslation();
  const [selectedSegment, setSelectedSegment] = useState(1);
  const [selectedTab, setSelectedTab] = useState<TabKey>("seats");
  const activeSegment = segments[selectedSegment - 1];
  const ssrFlights: SSRFlight<AggregatedMeal | AggregatedBaggage | AggregatedSeat>[] = activeSegment?.[selectedTab] ?? [];
  const grandTotal = addOnRows.reduce((sum, row) => sum + row.total, 0);
  const currency = addOnRows[0]?.currency ?? "";
  return (
    <div className="flex flex-col gap-4 p-4">
      <ModalSegment segments={segments} onChange={setSelectedSegment} value={selectedSegment} />
      <ModalTabs value={selectedTab} onChange={setSelectedTab} />
      <div className="bg-background-subtle flex flex-col gap-4 p-5">
        {ssrFlights.length === 0 ? (
          <div className="text-font-minimal py-4 text-center text-sm">
            {t("UserHistory.flight.noAddOnSelectedForSegment", { type: selectedTab })}
          </div>
        ) : (
          ssrFlights.map((ssrFlight, i) => <FlightSSRSection key={i} ssrFlight={ssrFlight} tab={selectedTab} />)
        )}
      </div>
      <div className="flex items-center justify-between">
        <div className="text-font-subtle text-lg font-semibold">{t("UserHistory.flight.totalValue")}</div>
        <div className="text-font-default font-semibold">
          {formatNumber(grandTotal)} {currency}
        </div>
      </div>
    </div>
  );
};

const Extras: FC<{ data?: FlightOrderDetails }> = ({ data }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const segments = data ? transformFlightOrderDetails(data) : [];
  if (!hasAnySSR(segments)) return null;
  const addOnRows = buildAddOnSummaryFromSegments(segments);
  const currency = addOnRows[0]?.currency ?? "";
  return (
    <>
      <PaymentChip rows={addOnRows} title={t("UserHistory.flight.selectedAddons")} currency={currency} onClick={() => setOpen(true)} />
      <Modal open={open} footer={null} closable onCancel={() => setOpen(false)} className="w-[95vw]! md:w-[40%]! md:min-w-200!">
        <ModalView segments={segments} addOnRows={addOnRows} />
      </Modal>
    </>
  );
};

const FARE_RULE_STYLES: StyleOptions = {
  columnStyles: "gap-4",
  pieceStyles: "flex flex-col gap-2",
  tableStyles: "w-full border-collapse overflow-x-scroll",
  cellStyles: "border border-border-default p-2 text-center align-center items-center",
  thStyles: "bg-background-subtle font-semibold whitespace-nowrap!",
  listStyles: "flex flex-col gap-1 ps-5 items-start",
  ulStyles: "list-disc",
  tdStyles: "whitespace-nowrap!",
  olStyles: "list-decimal",
  listLabelStyles: "block font-semibold",
  fieldsetStyles: "rounded-md border border-border-default p-3",
  legendStyles: "px-1 font-semibold",
  h1Styles: "font-semibold",
  h2Styles: "font-semibold",
  h3Styles: "font-semibold",
  h4Styles: "font-semibold",
  aStyles: "text-font-active underline",
  pairStyles: "flex gap-3 items-center",
  pairLabelStyles: "text-font-subtle",
  pairValueStyles: "font-semibold whitespace-nowrap",
};
const FARE_RULE_LEVEL_SIZES = ["text-lg", "text-base", "text-sm", "text-xs"];

const VOID_TAGS = new Set(["img", "hr", "input", "br", "wbr"]);
const SAFE_URL = /^(https?:|mailto:|tel:|\/|#)/i;

type LevelClass = (level: number) => string | undefined;

function safeAttrs(attrs: ElementNode["attrs"]) {
  if (!attrs) return undefined;
  const out: Record<string, string | number> = {};
  Object.entries(attrs).forEach(([name, value]) => {
    if ((name === "href" || name === "src") && !SAFE_URL.test(String(value).trim())) return;
    out[name] = value;
  });
  return out;
}
const InlineHtml: FC<{ html: string; className?: string; style?: ListNode["labelStyle"] }> = ({ html, className, style }) => {
  return <span className={className} style={style} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />;
};
const Pair: FC<{ pair: PairNode; html?: boolean; className?: string }> = ({ pair, html, className }) => {
  const part = (content: string, partClassName?: string, style?: ListNode["labelStyle"]) =>
    html ? (
      <InlineHtml html={content} className={partClassName} style={style} />
    ) : (
      <span className={partClassName} style={style}>
        {content}
      </span>
    );
  return (
    <span className={cn(className, pair.className)} style={pair.style}>
      {pair.label && part(`${pair.label}:`, pair.labelClassName, pair.labelStyle)}
      {pair.label && pair.value && " "}
      {pair.value && part(pair.value, pair.valueClassName, pair.valueStyle)}
    </span>
  );
};

const Label: FC<{ html: string; pair?: PairNode; className?: string; style?: ListNode["labelStyle"] }> = ({ html, pair, className, style }) => {
  if (!html) return null;
  if (pair) {
    return (
      <span className={cn(className)} style={style}>
        <Pair pair={pair} html />
      </span>
    );
  }
  return <InlineHtml html={html} className={cn(className)} style={style} />;
};

function renderTable(node: TableNode, key: number, lv: LevelClass) {
  return (
    <table key={key} className={cn(lv(node.level), node.className)} style={node.style}>
      <tbody>
        {node.rows.map((row, r) => (
          <tr key={r} className={row.className} style={row.style}>
            {row.cells.map((cell, c) =>
              createElement(
                cell.type,
                {
                  key: c,
                  colSpan: cell.colSpan,
                  rowSpan: cell.rowSpan,
                  className: cn("whitespace-pre-line", cell.className),
                  style: cell.style,
                },
                cell.pair ? <Pair pair={cell.pair} /> : cell.text,
              ),
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function renderListItem(item: ListItemNode, key: number, lv: LevelClass) {
  return (
    <li key={key} className={cn(lv(item.level), item.className)} style={item.style}>
      <Label html={item.label} pair={item.pair} className={item.labelClassName} style={item.labelStyle} />
      {item.children.map((sub, i) => renderList(sub, i, lv))}
    </li>
  );
}

function renderList(node: ListNode, key: number, lv: LevelClass) {
  return (
    <Fragment key={key}>
      <Label html={node.label} pair={node.pair} className={cn(lv(node.level), node.labelClassName)} style={node.labelStyle} />
      {createElement(
        node.type,
        { className: cn(lv(node.level), node.className), style: node.style },
        node.children.map((item, i) => renderListItem(item, i, lv)),
      )}
    </Fragment>
  );
}

function renderNode(node: ParsedNode, key: number, lv: LevelClass): ReactNode {
  if (node.type === "text") return <Fragment key={key}>{(node as TextNode).text}</Fragment>;
  if (node.type === "raw")
    return <div key={key} className={lv(node.level)} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize((node as RawNode).html) }} />;
  if (node.type === "pair") return <Pair key={key} pair={node as PairNode} className={lv(node.level)} />;
  if (node.type === "table") return renderTable(node as TableNode, key, lv);
  if (node.type === "ul" || node.type === "ol") return renderList(node as ListNode, key, lv);

  const el = node as ElementNode;
  const props = { key, className: cn(lv(el.level), el.className) || undefined, style: el.style, ...safeAttrs(el.attrs) };
  if (VOID_TAGS.has(el.type)) return createElement(el.type, props);
  return createElement(
    el.type,
    props,
    el.children.map((child, i) => renderNode(child, i, lv)),
  );
}

const ParsedHtml: FC<{ nodes: ParsedNode[]; className?: string; levelClassNames?: string[] }> = ({ nodes, className, levelClassNames }) => {
  const lv: LevelClass = (level) => levelClassNames?.[Math.min(level, levelClassNames.length - 1)];
  return (
    <div dir="auto" className={className}>
      {nodes.map((node, i) => renderNode(node, i, lv))}
    </div>
  );
};

const FareRuleDetail: FC<{ html: string }> = ({ html }) => {
  const nodes = useMemo(() => (html ? parse(html.replaceAll("\\n", "<br>"), FARE_RULE_STYLES) : null), [html]);
  if (!nodes) return <NoData />;
  return <ParsedHtml nodes={nodes} className="text-font-default flex flex-col gap-2 text-sm" levelClassNames={FARE_RULE_LEVEL_SIZES} />;
};

const FareRules: FC<{ fareRules?: FlightOrderFareRule[] }> = ({ fareRules }) => {
  if (!fareRules || fareRules.length == 0) return <Empty />;
  return fareRules.map((rule, index) => (
    <div key={`rule-${index}`} className="custom-scrollbar">
      <FareRuleDetail html={rule.ruleDetail} />
    </div>
  ));
};

const FareRulesTrigger: FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const { t } = useTranslation();
  return (
    <div className="flex">
      <button onClick={onOpen} className="text-font-active flex cursor-pointer items-center gap-1 text-sm font-semibold">
        <InfoIcon /> {t("Flights.details.fareRules.viewFareRules")}
      </button>
    </div>
  );
};

const FareRulesDrawerWeb: FC<{ fareRules?: FlightOrderFareRule[] }> = ({ fareRules }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <FareRulesTrigger onOpen={() => setOpen(true)} />
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        placement="right"
        size={"70%"}
        closable={{ placement: "end" }}
        title={t("Flights.details.fareRules.fareRules")}
      >
        <FareRules fareRules={fareRules} />
      </Drawer>
    </>
  );
};

const FareRulesDrawerMob: FC<{ fareRules?: FlightOrderFareRule[] }> = ({ fareRules }) => {
  const { t } = useTranslation();
  return (
    <Carrier
      label={t("Flights.details.fareRules.fareRules")}
      renderTrigger={({ open }) => <FareRulesTrigger onOpen={open} />}
      renderSheet={() => (
        <div className="p-4">
          <FareRules fareRules={fareRules} />
        </div>
      )}
    />
  );
};

const FareRulesDrawerComponent = withConditionalComponent(FareRulesDrawerWeb, FareRulesDrawerMob);

const FareRulesDrawer: FC<{ fareRules?: FlightOrderFareRule[] }> = ({ fareRules }) => {
  const isLargeScreen = Grid.useBreakpoint()[MOBILE_BREAKPOINT_KEY];
  return <FareRulesDrawerComponent trigger={isLargeScreen} props={{ fareRules }} />;
};

function PaymentDetailsSkeleton() {
  return (
    <BannerCard className="p-4! flex flex-col gap-4" rootClassName="bg-background-minimal!">
      <SkeletonBox className="h-5 w-36" />
      <div className="outline-border-subtle flex flex-col gap-2 rounded-md p-3 outline">
        <div className="flex items-center justify-between">
          <SkeletonBox className="h-4 w-28" />
          <SkeletonBox className="h-4 w-20" />
        </div>
        <div className="bg-border-subtle h-px w-full" />
        <div className="flex flex-col gap-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between gap-2">
              <SkeletonBox className="h-4 w-24" />
              <SkeletonBox className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    </BannerCard>
  );
}

const PaymentDetails: FC<{ data?: FlightOrderDetails; isFetching?: boolean }> = ({ data, isFetching }) => {
  const { t } = useTranslation();
  if (isFetching) return <PaymentDetailsSkeleton />;
  const segments = data ? transformFlightOrderDetails(data) : [];
  const addOnRows = buildAddOnSummaryFromSegments(segments);
  const currency = addOnRows[0]?.currency ?? "";
  const totalAmount = data?.totalAmount ?? 0;

  return (
    <div className="bg-background-default flex flex-col gap-3 rounded-sm p-5 shadow-sm">
      <div className="flex flex-col gap-4">
        <div className="text-lg font-medium">{t("UserHistory.flight.paymentDetails")}</div>
        <BreakdownCard
          header={{ label: t("UserHistory.flight.priceSummary"), value: `${formatNumber(totalAmount)} ${currency}` }}
          rows={[{ label: t("UserHistory.flight.baseFare"), value: `${formatNumber(totalAmount)} ${currency}` }]}
        />
        <Extras data={data} />
        <BreakdownCard
          header={{ label: t("UserHistory.flight.paymentBreakdown") }}
          rows={[
            {
              label: t("UserHistory.flight.pointsRedeemed"),
              value: `${formatNumber(data?.totalPointsRedeemed ?? 0)} ${t("UserHistory.ordersList.pts")}`,
            },
            { label: t("UserHistory.flight.cashPaid"), value: `${formatNumber(data?.cashAmountPaid ?? 0)} ${currency}` },
          ]}
        />
      </div>
      <div className="border-border-default! h-1! w-full! border-t border-dashed" />
      <FareRulesDrawer fareRules={data?.fareRules} />
      <ContactUs />
    </div>
  );
};

export default PaymentDetails;
